// crewData.js — mock crew generation + pure scoring/budget/OT (spec §3–§5).
import { fmtFt, at, MS } from './crewFormat';

export const YARD = { x: 50, y: 90 };

export function projectOT({ clockIn, lunchTakenMin, remServiceMin, remDriveMin, now }) {
  const MS = 60000;
  const projWorkAtNow = (now - clockIn) / MS - lunchTakenMin;
  const futureLunch = lunchTakenMin === 0 ? 30 : 0;
  const projTotalMin = projWorkAtNow + remServiceMin + remDriveMin;
  const projTotalHrs = projTotalMin / 60;
  let level = 'ontrack';
  if (projTotalHrs >= 9.0) level = 'will';
  else if (projTotalHrs >= 8.2) level = 'approach';
  const projMin = Math.max(0, Math.round(projTotalMin - 480));
  return { level, projHrs: +projTotalHrs.toFixed(1), projMin, futureLunch };
}

export function computeBudget(visits, drives) {
  const budget = { driving: 0, onsite: 0, yard: 0, tracked: 0, idle: 0, lunch: 0, break: 0 };
  (drives || []).forEach((d) => { budget.driving += d.min; });
  (visits || []).forEach((v) => { budget[v.category === 'jobsite' ? 'onsite' : v.category] += v.durMin; });
  budget.total = budget.driving + budget.onsite + budget.yard + budget.tracked + budget.idle + budget.lunch + budget.break;
  return budget;
}

export function scoreCrew(crew) {
  const { ot, vehicle, behindMin, idleMin, loc } = crew;
  const reasons = []; let score = 0;
  if (ot.level === 'will') { score += 30; reasons.push(`Projected ${ot.projHrs}h today — ${ot.projMin}m into overtime`); }
  else if (ot.level === 'approach') { score += 12; reasons.push(`On pace for ${ot.projHrs}h — tight against the 8h line`); }
  if (vehicle.faultCodes.length) { score += 20; reasons.push(`Check-engine: ${vehicle.faultCodes[0]}`); }
  if (vehicle.regDays < 0) { score += 22; reasons.push(`Registration expired ${-vehicle.regDays}d ago`); }
  else if (vehicle.regDays <= 25) { score += 8; reasons.push(`Registration expires in ${vehicle.regDays}d`); }
  if (vehicle.openRepairs.some((r) => r.priority === 'high')) { score += 15; reasons.push(`Open repair: ${vehicle.openRepairs.find((r) => r.priority === 'high').item}`); }
  if (vehicle.mtcOverdueMi) { score += 7; reasons.push(`Oil service ${vehicle.mtcOverdueMi.toLocaleString()} mi overdue`); }
  // `fuel != null` guard is intentional and mirrored in server crewScoring.js: Phase-2 real data has no fuel feed (fuel === null), and null < 25 is truthy in JS.
  if (vehicle.fuel != null && vehicle.fuel < 25) { score += 12; reasons.push(`Fuel at ${vehicle.fuel}%`); }
  if (behindMin >= 45) { score += 16; reasons.push(`${behindMin}m behind plan — ${crew.remaining} tickets left`); }
  else if (behindMin >= 15) { score += 7; reasons.push(`${behindMin}m behind plan`); }
  if (idleMin >= 35) { score += 9; reasons.push(`${idleMin}m idle this afternoon`); }
  if (loc.offSiteCount > 0) { score += 11; reasons.push(`${loc.budget.idle}m idle at ${loc.offSiteCount} unknown location${loc.offSiteCount > 1 ? 's' : ''}`); }
  if (loc.hangoutCount > 0) { const hg = loc.visits.find((v) => v.category === 'tracked'); score += 9; reasons.push(`Stopped at ${hg.name} (${hg.durMin}m) mid-route`); }
  if (loc.phoneSplitFlag) { score += 8; reasons.push(`Phone ${fmtFt(loc.phoneSplitFt)} from the truck${loc.phoneKnown ? ` (near ${loc.phoneKnown})` : ''}`); }
  const level = score >= 30 ? 'critical' : score >= 12 ? 'watch' : 'healthy';
  return { level, score: Math.round(score), reasons };
}

export function generateCrews() {
  const ANCHOR = at(13, 45); // "now" snapshot = 1:45 PM

  // ── seeded RNG ─────────────────────────────────────────────
  let seed = 77211;
  const rnd = () => { seed |= 0; seed = (seed + 0x6D2B79F5) | 0; let t = Math.imul(seed ^ (seed >>> 15), 1 | seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
  const ri = (a, b) => Math.floor(rnd() * (b - a + 1)) + a;
  const rf = (a, b) => rnd() * (b - a) + a;
  const pick = (arr) => arr[Math.floor(rnd() * arr.length)];
  const shuffle = (arr) => { const a = arr.slice(); for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; };

  // ── name & identity pools ──────────────────────────────────
  const FIRST = ['Joey', 'Javier', 'Hugo', 'Luis', 'Tony', 'Marcus', 'Danny', 'Cesar', 'Diego', 'Omar', 'Rafael', 'Andre', 'Victor', 'Mateo', 'Hector', 'Ivan', 'Sergio', 'Pablo', 'Ruben', 'Felix', 'Saul', 'Nestor', 'Adan', 'Bryce', 'Cole', 'Dustin', 'Trey', 'Wes', 'Kyle', 'Gabe'];
  const LAST = ['Blanco', 'Carlos', 'Zozoya', 'Ortega', 'Ramos', 'Webb', 'Flores', 'Mendez', 'Aguilar', 'Castro', 'Reyes', 'Vargas', 'Salas', 'Pineda', 'Cordova', 'Najera', 'Quintero', 'Bautista', 'Lozano', 'Trejo', 'Macias', 'Duarte', 'Cano', 'Hale', 'Briggs', 'Tanaka', 'Pruitt', 'Vance'];
  const ROLES = ['Foreman', 'Tech', 'Tech', 'Irrigation Tech', 'Spray Tech', 'Trainee'];
  const DIVISIONS = ['Maintenance', 'Maintenance', 'Maintenance', 'Specialty', 'Irrigation', 'Enhancement'];
  const TRUCKS = ['F-250', 'F-250', 'F-350 + Trailer', 'F-350 + Trailer', 'RAM 2500', 'Isuzu NPR', 'F-150'];
  const FAULTS = ['P0420 Catalyst Eff.', 'P0171 System Lean', 'DEF Level Low', 'P0455 EVAP Leak', 'TPMS — RR Tire', 'P0300 Misfire'];
  const REPAIRS = ['Brake pads — front', 'A/C compressor', 'Trailer light wiring', 'Mower deck belt', 'Hydraulic leak — liftgate', 'Windshield chip', 'Tire rotation', 'Alternator whine'];

  const SITE_LABELS = ['MCSO Investigation', 'ITR Court Complex', 'MCDOT Admin', 'Estrella Jail', 'Watkins Facility', 'Civic Center Plaza', 'County Annex', 'Public Works Yard', 'Health Dept Campus', 'Records Building', 'Fleet Services', 'Parks Maintenance', 'Library — Central', 'Court Annex East', 'DMV — Superstition', 'Sheriff Substation', 'Transit Center', 'Game & Fish HQ', 'Wildlife Office', 'Fund 100 Maint', 'Towers Jail', 'Durango Complex', 'Camelback Office Park', 'Tatum Medical Plaza'];
  const STREETS = ['W Durango St', 'S 28th Dr', 'W Watkins St', 'N Tatum Blvd', 'E Camelback Rd', 'N Scottsdale Rd', 'W Ray Rd', 'E Lincoln Dr', 'S 48th St', 'W Washington St', 'N 7th St', 'E McDowell Rd', 'S Central Ave', 'W Bell Rd'];

  // ── map geography (percent coords) ─────────────────────────
  const REGIONS = [
    { x: 24, y: 60 }, { x: 33, y: 38 }, { x: 18, y: 30 }, { x: 44, y: 22 },
    { x: 58, y: 16 }, { x: 72, y: 26 }, { x: 80, y: 44 }, { x: 70, y: 60 },
    { x: 60, y: 72 }, { x: 40, y: 70 }, { x: 28, y: 78 }, { x: 84, y: 68 },
  ];
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

  // Desert Flora avatar palette
  const AV_COLORS = ['#1f5e3a', '#c66a45', '#3454c1', '#6e3fb7', '#a87320', '#a9341d', '#164a2c'];

  // ── per-crew generation ────────────────────────────────────
  const usedNames = new Set();
  const makeName = () => { for (let i = 0; i < 50; i++) { const n = `${pick(FIRST)} ${pick(LAST)}`; if (!usedNames.has(n)) { usedNames.add(n); return n; } } return `${pick(FIRST)} ${pick(LAST)} ${ri(2, 9)}`; };
  const initials = (n) => n.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  const avColor = (n) => AV_COLORS[(n.charCodeAt(0) + n.charCodeAt(n.length - 1)) % AV_COLORS.length];

  const divCount = {};
  const crews = [];
  // NOTE: crew count is coupled to REGIONS.length (each crew indexes REGIONS[i]).
  // When real-data wiring lands, derive the bound from REGIONS or guard the index.
  for (let i = 0; i < 12; i++) {
    const division = DIVISIONS[i % DIVISIONS.length];
    divCount[division] = (divCount[division] || 0) + 1;
    const foreman = makeName();
    const name = `${division} ${String(divCount[division]).padStart(2, '0')}`;

    // archetype: most fine, some behind/OT, a couple vehicle problems
    const arch = rnd();

    // ── crew members ─────────────────────────────────────────
    const size = pick([2, 3, 3, 3, 4]);
    const roles = shuffle(ROLES).slice(0, size);
    if (!roles.includes('Foreman')) roles[0] = 'Foreman';
    const clockIn = at(6, ri(35, 68)); // ~6:35–7:08
    const members = [];
    let memberOnLunch = false, memberOnBreak = false;
    for (let m = 0; m < size; m++) {
      const isForeman = roles[m] === 'Foreman';
      const mname = isForeman ? foreman : makeName();
      // status distribution at 1:45pm
      let mstatus = 'onsite';
      const r = rnd();
      if (m === 0) mstatus = 'onsite';
      else if (!memberOnLunch && r < 0.14) { mstatus = 'lunch'; memberOnLunch = true; }
      else if (!memberOnBreak && r < 0.22) { mstatus = 'break'; memberOnBreak = true; }
      else mstatus = 'onsite';
      const since = mstatus === 'lunch' ? at(13, ri(15, 40)) : mstatus === 'break' ? at(13, ri(20, 44)) : clockIn;
      members.push({
        name: mname, role: roles[m], initials: initials(mname), color: avColor(mname),
        status: mstatus, since, clockIn,
        phone: `(602) 555-${ri(1000, 9989)}`,
      });
    }

    // ── tickets / schedule (designed for a believable ~7–10h day) ──
    const tier = arch < 0.55 ? 'healthy' : arch < 0.82 ? 'watch' : 'critical';
    const remainingCount = tier === 'healthy' ? pick([1, 1, 1, 2]) : tier === 'watch' ? 2 : pick([2, 2, 2, 3]);
    const avgServiceMin = tier === 'healthy' ? ri(35, 50) : tier === 'watch' ? ri(40, 52) : ri(44, 54);
    const done = ri(4, 7);
    const scheduled = done + remainingCount;
    const lastTicketAt = at(13, ri(2, 38));
    const lastTicket = pick(SITE_LABELS);

    // schedule adherence
    const behindMin = tier === 'healthy' ? ri(-12, 6) : tier === 'watch' ? ri(12, 30) : ri(30, 60);

    // ── map: region, completed breadcrumb, planned remaining ──
    const region = REGIONS[i];
    const allStops = [];
    const nStops = scheduled;
    for (let s = 0; s < nStops; s++) {
      allStops.push({
        x: Math.min(96, Math.max(4, region.x + rf(-13, 13))),
        y: Math.min(86, Math.max(6, region.y + rf(-12, 12))),
        label: pick(SITE_LABELS), addr: `${ri(100, 9800)} ${pick(STREETS)}`,
      });
    }
    // order by nearest-neighbour from yard
    const ordered = []; let cur = YARD; const rest = allStops.slice();
    while (rest.length) { let bi = 0, bd = Infinity; for (let k = 0; k < rest.length; k++) { const d = dist(cur, rest[k]); if (d < bd) { bd = d; bi = k; } } cur = rest[bi]; ordered.push(rest.splice(bi, 1)[0]); }
    const doneStops = ordered.slice(0, done);
    const remaining = ordered.slice(done);
    const currentPos = doneStops.length ? { x: doneStops[doneStops.length - 1].x, y: doneStops[doneStops.length - 1].y } : { ...YARD };

    // crew activity: most on-site; a few driving between stops
    let activity = 'onsite';
    if (memberOnLunch && members[0].status !== 'onsite') activity = 'lunch';
    if (rnd() < 0.28 && remaining.length) activity = 'driving';
    if (members.every((mm) => mm.status === 'lunch')) activity = 'lunch';

    // if driving, place current pos partway to next stop
    if (activity === 'driving' && remaining.length) {
      const nx = remaining[0]; const tprog = rf(0.3, 0.7);
      currentPos.x = currentPos.x + (nx.x - currentPos.x) * tprog;
      currentPos.y = currentPos.y + (nx.y - currentPos.y) * tprog;
    }

    // breadcrumb = yard → done stops → currentPos, densified with jitter
    const crumbAnchors = [YARD, ...doneStops, currentPos];
    const breadcrumb = [];
    for (let a = 0; a < crumbAnchors.length - 1; a++) {
      const p0 = crumbAnchors[a], p1 = crumbAnchors[a + 1];
      const segs = 4;
      for (let s = 0; s <= segs; s++) {
        const t = s / segs;
        breadcrumb.push({
          x: p0.x + (p1.x - p0.x) * t + (s > 0 && s < segs ? rf(-1.6, 1.6) : 0),
          y: p0.y + (p1.y - p0.y) * t + (s > 0 && s < segs ? rf(-1.6, 1.6) : 0),
        });
      }
    }

    // remaining drive: sum distances current→remaining stops (capped to stay believable)
    let driveUnits = 0; let c2 = currentPos;
    for (const r2 of remaining) { driveUnits += dist(c2, r2); c2 = r2; }
    const remDriveMin = Math.min(38, Math.round(driveUnits * 1.4));
    const remServiceMin = remaining.length * avgServiceMin;

    // ── GPS / location intelligence (mirrors the fleet-tracking model) ──
    // lunch state is needed both here (visits) and in the OT projection below
    const lunchOnNow = members.some((mm) => mm.status === 'lunch');
    const lunchTakenMin = lunchOnNow ? 0 : 30; // most crews logged lunch by 1:45
    // Each visit is a dwell at a place, classed Yard / Jobsite / Tracked / Idle.
    // Driving lives in the connectors between visits. The budget sums it all.
    const geofenceFt = 150;
    const HANGOUTS = ['Circle K #4821', 'QuikTrip #1140', 'Shell — Power Rd', 'Chevron — Baseline', "Filiberto's", 'Circle K #2207', 'QuikTrip #0098', 'Home Depot lot'];
    const YARDS = ['HMI Yard', 'ADOT Yard', 'Operations Yard'];
    const visits = []; const drives = [];
    let vcur = clockIn;
    const addDrive = (m) => { if (m > 0 && vcur < ANCHOR) { drives.push({ min: m, fromT: vcur }); vcur += m * MS; } };
    const addVisit = (vname, category, durMin, opts = {}) => {
      if (vcur >= ANCHOR) return;
      const start = vcur; const end = Math.min(vcur + durMin * MS, ANCHOR);
      const dur = Math.max(1, Math.round((end - start) / MS));
      visits.push({ name: vname, category, startT: start, endT: end, durMin: dur, idlingMin: opts.idling || 0, fromCenterFt: opts.fromCenterFt != null ? opts.fromCenterFt : ri(10, 90), insideFt: opts.insideFt != null ? opts.insideFt : null, known: !!opts.known });
      vcur = end;
    };
    // morning yard load-up
    addVisit(pick(YARDS), 'yard', ri(18, 34), { idling: ri(2, 10), fromCenterFt: ri(20, 120), insideFt: ri(300, 600) });
    // run the completed jobs, with the occasional detour
    const jobN = Math.min(doneStops.length, tier === 'healthy' ? 4 : 3);
    const lunchAt = Math.floor(jobN / 2);
    let breakPlaced = false;
    for (let j = 0; j < jobN && vcur < ANCHOR - 4 * MS; j++) {
      addDrive(ri(8, 17));
      const fr = rnd();
      if ((tier === 'critical' && fr < 0.5) || (tier === 'watch' && fr < 0.24) || (tier === 'healthy' && fr < 0.06)) {
        if (rnd() < 0.5) { addDrive(ri(2, 6)); addVisit(pick(HANGOUTS), 'tracked', ri(10, 26), { idling: ri(5, 18), fromCenterFt: ri(15, 60), insideFt: ri(20, 80), known: true }); }
        else { addVisit('Unknown Location', 'idle', ri(18, 80), { idling: ri(8, 35), fromCenterFt: ri(400, 4200) }); }
      }
      // midday lunch (crews that already took it); a short break otherwise
      if (j === lunchAt && lunchTakenMin === 30) { addVisit('Lunch', 'lunch', ri(28, 34), { idling: ri(3, 10), fromCenterFt: ri(20, 320) }); addDrive(ri(2, 7)); }
      else if (!breakPlaced && rnd() < 0.32) { addVisit('Break', 'break', ri(8, 15), { idling: ri(2, 8), fromCenterFt: ri(15, 160) }); breakPlaced = true; }
      addVisit(doneStops[j].label, 'jobsite', Math.round(avgServiceMin * rf(0.7, 1.15)), { fromCenterFt: ri(8, 95), insideFt: ri(70, 145) });
    }
    // trailing time up to "now" — current activity
    if (vcur < ANCHOR) {
      const tail = Math.round((ANCHOR - vcur) / MS);
      if (activity === 'driving') addDrive(tail);
      else addVisit(activity === 'lunch' ? 'Lunch' : (doneStops.length ? doneStops[doneStops.length - 1].label : 'On site'), activity === 'lunch' ? 'lunch' : 'jobsite', tail, { idling: activity === 'lunch' ? ri(5, 15) : 0, fromCenterFt: ri(10, 90), insideFt: activity === 'lunch' ? null : ri(70, 145) });
    }

    // budget by category (minutes)
    const budget = computeBudget(visits, drives);
    const budgetTotal = budget.total;

    // current phone-vs-vehicle separation
    let phoneSplitFt = ri(20, 160); let phoneKnown = null;
    if ((tier === 'critical' && rnd() < 0.45) || (tier === 'watch' && rnd() < 0.15)) {
      phoneSplitFt = ri(1100, 4200);
      if (rnd() < 0.5) phoneKnown = pick(HANGOUTS);
    }
    const phoneSplitFlag = phoneSplitFt > 800;
    const splitPct = phoneSplitFlag ? Math.min(11, Math.max(2, phoneSplitFt / 550)) : rf(0.2, 0.6);
    const sang = rf(0, Math.PI * 2);
    const phonePos = { x: Math.min(97, Math.max(3, currentPos.x + Math.cos(sang) * splitPct)), y: Math.min(92, Math.max(4, currentPos.y + Math.sin(sang) * splitPct)) };

    const hangoutCount = visits.filter((v) => v.category === 'tracked').length;
    const offSiteCount = visits.filter((v) => v.category === 'idle').length;
    const loc = { geofenceFt, visits, drives, budget, budgetTotal, phoneSplitFt, phoneKnown, phonePos, offSiteCount, hangoutCount, phoneSplitFlag, flags: offSiteCount + hangoutCount + (phoneSplitFlag ? 1 : 0) };

    // ── vehicle (issues skew toward at-risk crews; most run clean) ──
    const truckNo = `T-${100 + i * 7 + ri(0, 5)}`;
    let fuel = ri(45, 95);
    const faultCodes = [];
    const openRepairs = [];
    let mtcOverdueMi = 0;
    let regDays = ri(40, 300); // days until registration expiry (neg = expired)
    if (rnd() < (tier === 'critical' ? 0.55 : 0.08)) { faultCodes.push(pick(FAULTS)); fuel = ri(15, 40); }
    if (rnd() < 0.08) faultCodes.push(pick(FAULTS));
    if (rnd() < (tier === 'healthy' ? 0.12 : 0.32)) openRepairs.push({ item: pick(REPAIRS), priority: rnd() < 0.35 ? 'high' : 'normal', age: ri(1, 14) });
    if (rnd() < 0.18) openRepairs.push({ item: pick(REPAIRS), priority: 'normal', age: ri(1, 21) });
    if (rnd() < 0.22) mtcOverdueMi = ri(200, 2400);
    if (rnd() < 0.1) regDays = -ri(2, 40); // expired
    else if (rnd() < 0.16) regDays = ri(3, 25); // expiring soon
    const odometer = ri(48000, 142000);
    const vehicle = {
      no: truckNo, type: pick(TRUCKS), fuel, odometer,
      faultCodes: [...new Set(faultCodes)],
      openRepairs, mtcOverdueMi, regDays,
    };

    // ── OT projection ────────────────────────────────────────
    const ot = projectOT({ clockIn, lunchTakenMin, remServiceMin, remDriveMin, now: ANCHOR });

    // idle time
    const idleMin = tier === 'critical' ? ri(25, 55) : tier === 'watch' ? ri(8, 26) : ri(0, 12);

    // ── crew status scoring (drives the plant glyph) ─────────
    const status = scoreCrew({ ot, vehicle, behindMin, idleMin, loc, remaining: remaining.length });

    crews.push({
      id: `crew-${i}`, name, division, foreman,
      members, activity,
      vehicle,
      location: { label: activity === 'driving' ? `En route to ${remaining[0] ? remaining[0].label : 'next stop'}` : (doneStops.length ? doneStops[doneStops.length - 1].label : 'Yard'), addr: doneStops.length ? doneStops[doneStops.length - 1].addr : '— Operations Yard', x: currentPos.x, y: currentPos.y },
      lastTicket: { label: lastTicket, at: lastTicketAt },
      ticketsDone: done, ticketsScheduled: scheduled, avgServiceMin,
      behindMin, idleMin,
      clockIn,
      remServiceMin, remDriveMin, remaining: remaining.length,
      ot: { level: ot.level, projHrs: ot.projHrs, projMin: ot.projMin, futureLunch: ot.futureLunch },
      breadcrumb, plannedStops: remaining, doneStops, currentPos, loc,
      status,
    });
  }

  // worst first
  crews.sort((a, b) => b.status.score - a.status.score);
  return crews;
}

export function computeFleet(crews) {
  const allMembers = crews.flatMap((c) => c.members);
  return {
    crews: crews.length,
    membersOn: allMembers.filter((m) => m.status !== 'off').length,
    onLunch: allMembers.filter((m) => m.status === 'lunch').length,
    onBreak: allMembers.filter((m) => m.status === 'break').length,
    driving: crews.filter((c) => c.activity === 'driving').length,
    ticketsDone: crews.reduce((a, c) => a + c.ticketsDone, 0),
    ticketsSched: crews.reduce((a, c) => a + c.ticketsScheduled, 0),
    behind: crews.filter((c) => c.behindMin >= 15).length,
    otRisk: crews.filter((c) => c.ot.level !== 'ontrack').length,
    otWill: crews.filter((c) => c.ot.level === 'will').length,
    // `fuel != null` guard mirrors server crewScoring.js (Phase-2 fuel === null when no feed; null < 25 is truthy).
    vehicleAlerts: crews.filter((c) => c.vehicle.faultCodes.length || c.vehicle.regDays < 0 || c.vehicle.openRepairs.some((r) => r.priority === 'high') || (c.vehicle.fuel != null && c.vehicle.fuel < 25) || c.vehicle.mtcOverdueMi).length,
    locationFlags: crews.filter((c) => c.loc.flags > 0).length,
    idleHrs: +(crews.reduce((a, c) => a + c.loc.budget.idle, 0) / 60).toFixed(1),
    onsiteHrs: +(crews.reduce((a, c) => a + c.loc.budget.onsite, 0) / 60).toFixed(1),
    healthy: crews.filter((c) => c.status.level === 'healthy').length,
    watch: crews.filter((c) => c.status.level === 'watch').length,
    critical: crews.filter((c) => c.status.level === 'critical').length,
  };
}
